import { type ReadonlySignal, Signal, useComputed } from "@suse-o11y/signal";
import type { Size } from "@suse-o11y/size";
import { useRequiredContext } from "@suse-o11y/use-required-context";
import Konva from "konva";
import React, { useMemo } from "react";

export interface ContainerContextValue {
	instance: Konva.Container;
	size: ReadonlySignal<Size>;
}

const ContainerContext = React.createContext<null | ContainerContextValue>(null);

export const ContainerContextProvider = ContainerContext.Provider;

export const useInContainer = (node: Konva.Node): void => {
	const context = React.useContext(ContainerContext);

	React.useLayoutEffect(() => {
		const isStage = node instanceof Konva.Stage;
		// Stage is an root container, so it is expected
		if (isStage && context == null) {
			return;
		}

		if (context == null) {
			throw new Error("Expecting <Container> in the component tree");
		}

		if (isStage) {
			throw new Error("Stage can't have another Stage");
		}

		context.instance.add(node);

		return () => {
			node.remove();
		};
	}, [context, node]);
};

export const useSize = (ownSize: Partial<Size>): ReadonlySignal<Size> => {
	const parent = React.useContext(ContainerContext);

	return useMemo(
		() =>
			Signal((monitor) => ({
				width: ownSize.width ?? parent?.size.read(monitor).width ?? 0,
				height: ownSize.height ?? parent?.size.read(monitor).height ?? 0,
			})),
		[ownSize.width, ownSize.height, parent?.size],
	);
};

export const useContainerSize = (): Size => {
	const { size } = useRequiredContext(ContainerContext, {
		providerName: "Container",
	});

	return useComputed(size);
};
